/**
 * TicketEquipmentSections.tsx
 *
 * "Equipment on this Ticket" section for Repair / Replace / Return tickets.
 * Data comes from the ticket detail already in Redux (ticket.equipment),
 * which is populated by the main GET /api/ticketing/tickets/{ticketNumber} call.
 * No secondary API call is needed.
 *
 * Design source: ticketing-portal-web-v32.html eqDetailSections
 */
import React, { useState } from "react";
import {
  ActivityIndicator, Modal, Platform, Pressable, StyleSheet, Text, TextInput, View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import {
  TicketEquipmentData,
  TicketEquipmentLine,
} from "../../../store/slices/ticketingPortalSlice";
import {
  EQUIPMENT_CATALOG,
  EquipmentCatalogItem,
} from "../data/repairReplaceReturnEquipmentCatalog";
import { useToast } from "../../../components/LhToastContainer";
import {
  exportInventory,
  savePackingSlipNumber,
  SLIP_NUMBER_PATTERN,
} from "../../../api/packingSlipApi";

// --- Helpers ---------------------------------------------------------------

function lookupCatalog(sku: string): EquipmentCatalogItem | undefined {
  return EQUIPMENT_CATALOG.find(
    (c) => c.sku.toUpperCase() === sku.toUpperCase()
  );
}

function eqPathLabel(path: string): string {
  switch (path) {
    case "RMA":     return "RMA Repair or Replacement";
    case "NON_RMA": return "Non-RMA Replacement (End of Life)";
    case "RECLAIM": return "Voice to Vision Reclaim Return";
    default:        return path;
  }
}

function lineUnits(line: TicketEquipmentLine): number {
  if (line.serialNumber) return 1;
  return (line.quantityGood ?? 0) + (line.quantityBroken ?? 0);
}

function fmt(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// --- Props ------------------------------------------------------------------
interface Props {
  /** equipment block from the ticket detail record in Redux */
  equipment: TicketEquipmentData;
  /** Display location pulled from ticket.location */
  location: string;
  /** Numeric backend ticket ID — required to run the Inventory Export API call. */
  ticketId?: number;
  /** Human-readable ticket number, used in the Inventory Export fallback toast. */
  ticketNumber?: string;
  /** Sustain Team members only may run the Inventory Export action. */
  canRunInventoryExport?: boolean;
  /** Called when the Packing Slip action should open the Packing Slip screen. */
  onOpenPackingSlip?: () => void;
}

// --- Main component ----------------------------------------------------------
const TicketEquipmentSections: React.FC<Props> = ({
  equipment, location, ticketId, ticketNumber, canRunInventoryExport, onOpenPackingSlip,
}) => {
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [slipNumberInput, setSlipNumberInput] = useState("");
  const [slipNumberError, setSlipNumberError] = useState<string | null>(null);
  const [isSavingSlip, setIsSavingSlip] = useState(false);
  const lines = equipment.lines ?? [];
  if (lines.length === 0) return null;

  const totalUnits = lines.reduce((s, l) => s + lineUnits(l), 0);
  const eqPathLbl  = eqPathLabel(equipment.equipmentPath);
  const vendorName = equipment.vendor ?? "-";
  
  // Build units breakdown text from unitsBreakdown or calculate from lines
  const buildUnitsText = () => {
    if (equipment.unitsBreakdown) {
      const { total, shipping, eWaste } = equipment.unitsBreakdown;
      return `${total} total, ${shipping} shipping, ${eWaste} e-waste`;
    }
    return String(totalUnits);
  };

  const handleInventoryExport = async () => {
    if (!ticketId || isExporting) return;
    setIsExporting(true);
    const r = await exportInventory(ticketId);
    setIsExporting(false);
    if (r.error) { toast.show(r.error, "error"); return; }
    const msg = r.data?.message
      ?? `Inventory export queued for ${ticketNumber ?? "this ticket"}: ${totalUnits} units.`;
    toast.show(msg, "success");
  };

  const handlePackingSlip = () => {
    if (!equipment.vendor) {
      toast.show("Nothing ships to a vendor on this ticket, so there is no packing slip.", "info");
      return;
    }
    setSlipNumberInput("");
    setSlipNumberError(null);
    setShowSlipModal(true);
  };

  const handleSubmitSlipNumber = async () => {
    if (!ticketId || isSavingSlip) return;
    const value = slipNumberInput.trim().toUpperCase();
    if (!value) { setSlipNumberError("Packing slip number is required."); return; }
    if (!SLIP_NUMBER_PATTERN.test(value)) {
      setSlipNumberError("Use up to 20 letters, digits, -, _ or /.");
      return;
    }
    setSlipNumberError(null);
    setIsSavingSlip(true);
    const r = await savePackingSlipNumber(ticketId, value);
    setIsSavingSlip(false);
    if (r.error || !r.data) { setSlipNumberError(r.error ?? "Failed to save the packing slip number."); return; }
    setShowSlipModal(false);
    onOpenPackingSlip?.();
  };

  return (
    <View style={e.card}>
      {/* Header */}
      <View style={e.cardHdr}>
        <View style={e.cardHdrLeft}>
          <MaterialIcons name="inventory" size={16} color="#374151" />
          <Text style={e.cardTitle}>Equipment on this Ticket</Text>
          <Text style={e.cardSub}> - {totalUnits} unit{totalUnits !== 1 ? "s" : ""}, read only</Text>
        </View>
        <View style={e.cardHdrActions}>
          {canRunInventoryExport && (
            <Pressable
              style={({ pressed }) => [e.ghostBtn, pressed && e.btnPressed, isExporting && e.btnDisabled]}
              onPress={handleInventoryExport}
              disabled={isExporting}
              accessibilityRole="button"
            >
              {isExporting ? (
                <ActivityIndicator size="small" color="#374151" />
              ) : (
                <Text style={e.ghostBtnText}>Inventory Export</Text>
              )}
            </Pressable>
          )}
          <Pressable
            style={({ pressed }) => [e.secondaryBtn, pressed && e.btnPressed]}
            onPress={handlePackingSlip}
            accessibilityRole="button"
          >
            <Text style={e.secondaryBtnText}>Packing Slip</Text>
          </Pressable>
        </View>
      </View>

      <View style={e.cardBody}>
        {/* KV summary - 2 column grid */}
        <View style={e.kvGrid}>
          <KvPair label="Equipment Request Type" value={eqPathLbl} />
          <KvPair label="Location" value={location} />
          <KvPair label="Units Logged" value={buildUnitsText()} />
          <KvPair label="Vendor" value={vendorName} />
        </View>

        {/* Replacement Expected - Full width badge */}
        {equipment.replacementExpected && (
          <View style={e.kvGrid}>
            <View style={e.kvFullWidth}>
              <Text style={e.kvLbl}>Replacement Expected</Text>
              <View style={e.badgeContainer}>
                <Text style={e.badgeText}>{equipment.replacementExpected}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Vendor Destination - Full width badge */}
        {equipment.vendorDestination && (
          <View style={e.kvGrid}>
            <View style={e.kvFullWidth}>
              <Text style={e.kvLbl}>Vendor Destination</Text>
              <View style={e.badgeContainerPurple}>
                <Text style={e.badgeText}>{equipment.vendorDestination}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Table */}
        <View style={{ width: "100%" }}>
          <View style={e.tblHdr}>
              <Text style={[e.tblTh, { flex: 1.4 }]}>SKU</Text>
              <Text style={[e.tblTh, { flex: 2 }]}>Description</Text>
              <Text style={[e.tblTh, { flex: 1.2 }]}>Type</Text>
              <Text style={e.tblTh}>GEN</Text>
              <Text style={e.tblTh}>Capture</Text>
              <Text style={e.tblTh}>Units</Text>
              <Text style={e.tblTh}>Routes To</Text>
              <Text style={[e.tblTh, { flex: 2 }]}>Serial Numbers</Text>
          </View>
            {lines.map((line) => (
              <EquipmentLineRow key={line.id} line={line} />
            ))}
        </View>
      </View>

      <Modal
        transparent
        visible={showSlipModal}
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setShowSlipModal(false)}
      >
        <View style={e.modalBackdrop}>
          <View style={e.modalCard}>
            <Text style={e.modalTitle}>Enter Packing Slip Number</Text>
            <Text style={e.modalBody}>
              Type the packing slip number for this shipment. It will be printed on the slip.
            </Text>
            <Text style={e.modalLabel}>Packing Slip Number</Text>
            <TextInput
              style={[e.modalInput, slipNumberError ? e.modalInputError : null]}
              value={slipNumberInput}
              onChangeText={(v) => { setSlipNumberInput(v.toUpperCase()); setSlipNumberError(null); }}
              placeholder="e.g. PL-02-097"
              placeholderTextColor="#9ca3af"
              maxLength={20}
              autoCapitalize="characters"
              autoCorrect={false}
              autoFocus
              editable={!isSavingSlip}
              onSubmitEditing={handleSubmitSlipNumber}
            />
            {slipNumberError ? <Text style={e.modalError}>{slipNumberError}</Text> : null}
            <View style={e.modalActions}>
              <Pressable
                style={({ pressed }) => [e.ghostBtn, pressed && e.btnPressed]}
                onPress={() => setShowSlipModal(false)}
                disabled={isSavingSlip}
              >
                <Text style={e.ghostBtnText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [e.secondaryBtn, pressed && e.btnPressed, isSavingSlip && e.btnDisabled]}
                onPress={handleSubmitSlipNumber}
                disabled={isSavingSlip}
              >
                {isSavingSlip ? (
                  <ActivityIndicator size="small" color="#374151" />
                ) : (
                  <Text style={e.secondaryBtnText}>Generate Slip</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// --- Per-line row sub-component ------------------------------------------------
const EquipmentLineRow: React.FC<{ line: TicketEquipmentLine }> = ({ line }) => {
  const catalog = lookupCatalog(line.sku);
  const desc     = catalog?.desc ?? line.sku;
  const etype    = line.type ?? catalog?.etype ?? "-";  // Prioritize API type field
  const capture  = line.dataCapture ?? catalog?.capture ?? (line.serialNumber ? "serial" : "quantity");
  const units    = lineUnits(line);
  const isSerial = capture === "serial" || capture === "SERIAL";
  const [expanded, setExpanded] = useState(false);

  // Format units display with condition info
  const getUnitsDisplay = () => {
    if (line.condition) {
      // Extract condition type from condition string (e.g., "Good, ship to vendor" -> "shipping")
      const condLower = line.condition.toLowerCase();
      if (condLower.includes("ship")) return `${units} shipping`;
      if (condLower.includes("e-waste")) return `${units} e-waste`;
    }
    return String(units);
  };

  return (
    <>
      <Pressable
        style={e.tblRow}
        onPress={() => setExpanded((v) => !v)}
        accessibilityRole="button"
      >
        {/* Unreadable serial warning dot */}
        <View style={[e.tblTd, { flex: 1.4 }]}>
          <Text
            style={[e.monoText, line.serialUnreadable && { color: "#d97706" }]}
            numberOfLines={1}
          >
            {line.sku}
          </Text>
          {line.serialUnreadable && (
            <View style={e.warnDot}>
              <MaterialIcons name="warning" size={10} color="#d97706" />
            </View>
          )}
        </View>
        <Text style={[e.tblTd, { flex: 2, fontWeight: "600" }]} numberOfLines={1}>
          {desc}
        </Text>
        <Text style={[e.tblTd, { flex: 1.2 }]} numberOfLines={1}>
          {etype}
        </Text>
        <Text style={e.tblTd} numberOfLines={1}>
          {line.generation ?? "-"}
        </Text>
        <View style={e.tblTd}>
          <View style={[e.capPill, isSerial && e.capPillSerial]}>
            <Text style={[e.capPillTxt, isSerial && e.capPillTxtSerial]}>
              {isSerial ? "Serial" : "Qty"}
            </Text>
          </View>
        </View>
        <Text style={e.tblTd}>{getUnitsDisplay()}</Text>
        <Text style={e.tblTd} numberOfLines={1}>
          {line.routesTo ?? "-"}
        </Text>
        <View style={[e.tblTd, { flex: 2 }]}>
          {isSerial && line.serialNumber ? (
            <View style={e.chip}>
              <Text style={e.chipTxt}>{line.serialNumber}</Text>
            </View>
          ) : isSerial ? (
            <Text style={e.muted}>No serial</Text>
          ) : (
            <Text style={e.tblTd}>
              {line.quantityGood > 0 ? `${line.quantityGood} good` : ""}
              {line.quantityGood > 0 && line.quantityBroken > 0 ? ", " : ""}
              {line.quantityBroken > 0 ? `${line.quantityBroken} broken` : ""}
            </Text>
          )}
        </View>
        <MaterialIcons
          name={expanded ? "keyboard-arrow-up" : "keyboard-arrow-down"}
          size={16}
          color="#9ca3af"
          style={{ marginLeft: 4 }}
        />
      </Pressable>

      {/* Expanded detail row */}
      {expanded && (
        <View style={e.detailRow}>
          {line.condition ? (
            <DetailItem label="Condition" value={line.condition} />
          ) : null}
          {line.damageNotes ? (
            <DetailItem label="Damage Notes" value={line.damageNotes} />
          ) : null}
          {line.failureNotes ? (
            <DetailItem label="Failure Notes" value={line.failureNotes} />
          ) : null}
          {line.wesContactTime ? (
            <DetailItem
              label="WES Contact Time"
              value={`${fmt(line.wesContactTime)}${
                line.wesTimeZone ? ` (${line.wesTimeZone})` : ""
              }`}
            />
          ) : null}
          {line.serialUnreadable ? (
            <View style={e.warnBanner}>
              <MaterialIcons name="warning" size={13} color="#d97706" />
              <Text style={e.warnBannerText}>Serial number unreadable</Text>
            </View>
          ) : null}
        </View>
      )}
    </>
  );
};

const DetailItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <View style={e.detailItem}>
    <Text style={e.detailLabel}>{label}</Text>
    <Text style={e.detailValue}>{value}</Text>
  </View>
);

const KvPair: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <View style={e.kv}>
    <Text style={e.kvLbl}>{label}</Text>
    <Text style={e.kvVal}>{value}</Text>
  </View>
);

// --- Styles ------------------------------------------------------------------
const e = StyleSheet.create({
  modalBackdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.45)", alignItems: "center", justifyContent: "center", padding: 24 },
  modalCard: { backgroundColor: "#ffffff", borderRadius: 14, padding: 24, width: "100%", maxWidth: 420 },
  modalTitle: { fontSize: 18, fontWeight: "700", color: "#111827", marginBottom: 8 },
  modalBody: { fontSize: 14, color: "#374151", lineHeight: 21, marginBottom: 16 },
  modalLabel: { fontSize: 11, fontWeight: "700", color: "#6b7280", letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 6 },
  modalInput: { borderWidth: 1, borderColor: "#d1d5db", borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: "#111827", backgroundColor: "#ffffff" },
  modalInputError: { borderColor: "#b42318" },
  modalError: { fontSize: 12, color: "#b42318", marginTop: 6 },
  modalActions: { flexDirection: "row", justifyContent: "flex-end", gap: 10, marginTop: 20 },
  // Card shell
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    marginBottom: 16,
    overflow: "hidden",
  },
  cardHdr: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  cardHdrLeft: { flexDirection: "row", alignItems: "center", gap: 8, flex: 1 },
  cardTitle: { fontSize: 15, fontWeight: "700", color: "#374151" },
  cardSub: { fontSize: 12, color: "#9ca3af" },
  cardHdrActions: { flexDirection: "row", alignItems: "center", gap: 10 },
  ghostBtn: { paddingHorizontal: 10, paddingVertical: 7, borderRadius: 6 },
  ghostBtnText: { fontSize: 12.5, fontWeight: "600", color: "#374151" },
  secondaryBtn: {
    borderWidth: 1,
    borderColor: "#034895",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  secondaryBtnText: { fontSize: 12.5, fontWeight: "700", color: "#034895" },
  btnPressed: { opacity: 0.65 },
  btnDisabled: { opacity: 0.5 },
  cardBody: { padding: 16 },
  // KV summary grid - 2 column layout
  kvGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  kv: { flex: 1, minWidth: "45%" },
  kvFullWidth: { width: "100%" },
  kvLbl: {
    fontSize: 10,
    fontWeight: "700",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  kvVal: { fontSize: 13, color: "#374151", fontWeight: "500" },
  // Badge styles
  badgeContainer: {
    alignSelf: "flex-start",
    backgroundColor: "#e8f0fe",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  badgeContainerPurple: {
    alignSelf: "flex-start",
    backgroundColor: "#ede9f6",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#1e3a8a",
  },
  // Table
  tblHdr: {
    flexDirection: "row",
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    paddingVertical: 8,
    paddingHorizontal: 4,
    alignItems: "center",
  },
  tblTh: {
    fontSize: 10,
    fontWeight: "700",
    color: "#656565",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    flex: 0.8,
    paddingHorizontal: 4,
  },
  tblRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 11,
    paddingHorizontal: 4,
    alignItems: "center",
  },
  tblTd: {
    fontSize: 12,
    color: "#374151",
    flex: 0.8,
    paddingHorizontal: 4,
  },
  monoText: {
    fontSize: 11,
    fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
    color: "#374151",
  },
  warnDot: {
    position: "absolute" as any,
    top: -2,
    right: -2,
  },
  // Capture pills
  capPill: {
    backgroundColor: "#FEF1B9",
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    alignSelf: "flex-start",
  },
  capPillSerial: { backgroundColor: "#CCEAF6" },
  capPillTxt: { fontSize: 10, fontWeight: "700", color: "#8B6508" },
  capPillTxtSerial: { color: "#0078A7" },
  // Serial chip
  chip: {
    backgroundColor: "#f0f0f0",
    borderRadius: 4,
    paddingHorizontal: 7,
    paddingVertical: 3,
    alignSelf: "flex-start",
  },
  chipTxt: {
    fontSize: 11,
    fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
    color: "#374151",
  },
  muted: { fontSize: 11, color: "#a8a8a8", fontStyle: "italic" },
  // Expanded detail rows
  detailRow: {
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 6,
  },
  detailItem: { flexDirection: "row", gap: 8, alignItems: "flex-start" },
  detailLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: "#9ca3af",
    textTransform: "uppercase",
    minWidth: 110,
  },
  detailValue: { fontSize: 12, color: "#374151", flex: 1, lineHeight: 18 },
  // Unreadable serial warning banner
  warnBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    backgroundColor: "#fffbeb",
    borderLeftWidth: 3,
    borderLeftColor: "#f59e0b",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  warnBannerText: { fontSize: 11, color: "#92400e", flex: 1 },
});

export default TicketEquipmentSections;
